import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CodeBlock, Icon, SidePanel } from '@mysaiva/core'
import { curlSnippet, formatDate } from '../../api-keys/format'
import type { ApiKeySecret } from '../../api-keys/apiKeys.types'
import { SecretBox } from './SecretBox'
import styles from './ApiKeys.module.scss'

interface KeyCreatedPanelProps {
  open: boolean
  result: ApiKeySecret | null
  /** Called with whether the secret was copied at least once. */
  onClose: (copied: boolean) => void
  onSecretCopied?: () => void
  /** `rotated` changes the title copy; the body is the same. */
  mode?: 'created' | 'rotated'
}

const SUMMARY_SCOPES = 3

/**
 * One-time secret reveal after a key was created. The secret is shown only
 * once, so the panel cannot be dismissed until it has been copied: the
 * close button, the Done action and Escape only appear / work after the
 * first copy (design annotation, review SAIV-11493).
 */
export function KeyCreatedPanel({
  open,
  result,
  onClose,
  onSecretCopied,
  mode = 'created'
}: KeyCreatedPanelProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  // The panel stays mounted between keys; every reveal starts un-copied.
  useEffect(() => {
    if (open) setCopied(false)
  }, [open])

  if (!result) return null
  const { key, secret } = result
  const shownScopes = key.scopes.slice(0, SUMMARY_SCOPES)
  const extra = key.scopes.length - shownScopes.length

  const handleCopied = () => {
    setCopied(true)
    onSecretCopied?.()
  }

  const close = () => {
    if (copied) onClose(true)
  }

  return (
    <SidePanel
      open={open}
      onClose={close}
      closable={copied}
      title={t(`developerPlatform.apiKeys.created.${mode}Title`)}
      description={t('developerPlatform.apiKeys.created.subtitle', {
        name: key.name,
        environment: t(`developerPlatform.apiKeys.environment.${key.environment}`),
        count: key.scopes.length
      })}
      icon={<Icon icon="CheckCircle" size={24} />}
      iconTone="success"
      // No footer at all until the secret was copied; then the Done action.
      footer={copied ? undefined : null}
      actions={{
        primary: {
          label: t('developerPlatform.apiKeys.actions.done'),
          onClick: close,
          'data-testid': 'key-created-done'
        }
      }}
      className={styles.panelTokens}
      mask={false}
      data-testid="key-created-panel"
    >
      <div className={styles.form} data-copied={copied || undefined}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {t('developerPlatform.apiKeys.created.secretKey')}
          </span>
          <SecretBox
            secret={secret}
            copyLabel={t('developerPlatform.apiKeys.actions.copySecret')}
            copiedLabel={t('developerPlatform.apiKeys.actions.copied')}
            onCopied={handleCopied}
            data-testid="key-secret"
          />
        </div>
        <CodeBlock
          variant="light"
          language="bash"
          title={t('developerPlatform.apiKeys.created.firstRequest')}
          code={curlSnippet(secret)}
          copyLabel={t('developerPlatform.apiKeys.actions.copySnippet')}
          copiedLabel={t('developerPlatform.apiKeys.actions.copied')}
          wrap
          data-testid="key-curl"
        />
        <div className={styles.warning} role="alert" data-testid="key-created-warning">
          <Icon icon="WarningCircle" size={20} />
          <span>{t('developerPlatform.apiKeys.created.warning')}</span>
        </div>
        <dl className={styles.summary} data-testid="key-created-summary">
          <div className={styles.summaryRow}>
            <dt>{t('developerPlatform.apiKeys.fields.name')}</dt>
            <dd>{key.name}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('developerPlatform.apiKeys.fields.environment')}</dt>
            <dd>{t(`developerPlatform.apiKeys.environment.${key.environment}`)}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('developerPlatform.apiKeys.fields.scopes')}</dt>
            <dd>
              {shownScopes.join(', ')}
              {extra > 0 && ` +${extra}`}
            </dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('developerPlatform.apiKeys.fields.expires')}</dt>
            <dd>
              {key.expiresAt
                ? formatDate(key.expiresAt)
                : t('developerPlatform.apiKeys.expiration.never')}
            </dd>
          </div>
        </dl>
      </div>
    </SidePanel>
  )
}
